import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FaUserShield,
  FaUserClock,
  FaFileMedicalAlt,
  FaExclamationCircle,
  FaFileInvoiceDollar,
  FaUserTie,
  FaUserGraduate,
  FaUsers,
  FaChartLine,
} from 'react-icons/fa';

import { getAdminStats } from '../services/dashboardService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import Icon from '../components/common/Icon';

import pageStyles from './Page.module.css'; // تنسيقات الصفحة العامة
import styles from '../components/dashboard/Dashboard.module.css'; // سنقوم بتحديث هذا الملف

// مكون بطاقة إحصائية معاد تصميمه
const StatCard: React.FC<{
  title: string;
  value: string | number;
  linkTo: string;
  icon: React.ReactNode;
  colorClass: string;
}> = ({ title, value, linkTo, icon, colorClass }) => (
  <Link to={linkTo} className={`${styles.card} ${styles[colorClass]}`}>
    <div className={styles.cardIcon}>{icon}</div>
    <div className={styles.cardContent}>
      <div className={styles.cardValue}>{value}</div>
      <div className={styles.cardTitle}>{title}</div>
    </div>
  </Link>
);

const AdminDashboard: React.FC = () => {
  const { data: stats, isLoading, isError, error } = useQuery({
    queryKey: ['adminDashboardStats'],
    queryFn: getAdminStats,
  });

  if (isLoading) return <LoadingSpinner fullScreen />;
  if (isError) return <ErrorAlert message={error.message} />;

  return (
    <div className={pageStyles.pageContainer}>
      <h1 className={pageStyles.pageTitle}>لوحة التحكم الرئيسية</h1>
      
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>إجراءات عاجلة</h2>
        <div className={styles.grid}>
          <StatCard 
            title="مستخدمون بانتظار الموافقة" 
            value={stats?.pendingApprovals ?? 0} 
            linkTo="/admin/user-approvals"
            icon={<Icon as={FaUserClock} />}
            colorClass="colorWarning"
          />
          <StatCard 
            title="طلبات خدمات جديدة" 
            value={stats?.pendingServiceRequests ?? 0} 
            linkTo="/admin/service-requests" 
            icon={<Icon as={FaFileMedicalAlt} />}
            colorClass="colorWarning"
          />
          <StatCard 
            title="شكاوى جديدة" 
            value={stats?.pendingComplaints ?? 0} 
            linkTo="/admin/complaints"
            icon={<Icon as={FaExclamationCircle} />}
            colorClass="colorDanger"
          />
          <StatCard 
            title="فواتير مستحقة" 
            value={stats?.dueInvoices ?? 0} 
            linkTo="/admin/invoices?status=pending"
            icon={<Icon as={FaFileInvoiceDollar} />}
            colorClass="colorDanger"
          />
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>نظرة عامة على النظام</h2>
        <div className={styles.grid}>
          <StatCard 
            title="إجمالي المحامين" 
            value={stats?.totalLawyers ?? 0} 
            linkTo="/admin/lawyers" 
            icon={<Icon as={FaUserTie} />}
            colorClass="colorPrimary"
          />
          <StatCard 
            title="إجمالي المتدربين" 
            value={stats?.totalTrainees ?? 0} 
            linkTo="/admin/trainees" 
            icon={<Icon as={FaUserGraduate} />}
            colorClass="colorPrimary"
          />
          <StatCard 
            title="إجمالي المشتركين" 
            value={stats?.totalSubscribers ?? 0} 
            linkTo="/admin/subscribers" 
            icon={<Icon as={FaUsers} />}
            colorClass="colorPrimary"
          />
          <StatCard 
            title="إيرادات هذا الشهر" 
            value={`$${(stats?.monthlyRevenue ?? 0).toFixed(2)}`} 
            linkTo="/admin/invoices?status=paid" 
            icon={<Icon as={FaChartLine} />}
            colorClass="colorSuccess"
          />
        </div>
      </div>
    </div>
  );
};

// تأكد من أن المسار في App.tsx يستدعي هذا المكون
export default AdminDashboard;
